import React, { useEffect, useState } from 'react';
import { settingsStorage } from '../../services/localStorage.service';
import './Settings.css';

const Settings = () => {
  const defaultSettings = {
    notifications: true,
    emailNotifications: true,
    smsNotifications: false,
    darkMode: false,
    language: 'en',
    timezone: 'Asia/Kolkata',
    currency: 'INR',
    dateFormat: 'DD/MM/YYYY',
    autoSave: true,
    sessionTimeout: '30',
    email: '',
    phone: '',
    companyName: 'Shree Sainath Medical CRM',
    address: '',
  };

  const [settings, setSettings] = useState(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });

  // Load settings from localStorage on component mount
  useEffect(() => {
    const loadSettings = () => {
      try {
        const savedSettings = settingsStorage.get();
        if (savedSettings) {
          setSettings({ ...defaultSettings, ...savedSettings });
        } else {
          setSettings(defaultSettings);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        setSettings(defaultSettings);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings({
      ...settings,
      [name]: type === 'checkbox' ? checked : value,
    });
    // Clear save status when user makes changes
    setSaveStatus({ type: '', message: '' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaveStatus({ type: '', message: '' });
    
    try {
      const saved = settingsStorage.save(settings);
      
      if (saved) {
        // Apply dark mode if enabled
        if (settings.darkMode) {
          document.body.classList.add('dark-mode');
        } else {
          document.body.classList.remove('dark-mode');
        }

        setSaveStatus({ type: 'success', message: 'Settings saved successfully!' });
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSaveStatus({ type: '', message: '' });
        }, 3000);
      } else {
        setSaveStatus({ type: 'error', message: 'Failed to save settings. Please try again.' });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveStatus({ type: 'error', message: 'Failed to save settings. Please try again.' });
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to default values?')) {
      setSettings(defaultSettings);
      settingsStorage.save(defaultSettings);
      document.body.classList.remove('dark-mode');
      setSaveStatus({ type: 'success', message: 'Settings reset to default values!' });
      
      setTimeout(() => {
        setSaveStatus({ type: '', message: '' });
      }, 3000);
    }
  };

  if (isLoading) {
    return (
      <div className="settings">
        <div className="settings-loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="settings">
      <div className="settings-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Manage your account settings and preferences</p>
      </div>

      {saveStatus.message && (
        <div className={`save-status ${saveStatus.type}`}>
          {saveStatus.message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="settings-content">
        <div className="card">
          <h2 className="card-title">General Settings</h2>
          <div className="settings-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="language">Language</label>
                <select
                  id="language"
                  name="language"
                  value={settings.language}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="en">English</option>
                  <option value="hi">Hindi</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="timezone">Timezone</label>
                <select
                  id="timezone"
                  name="timezone"
                  value={settings.timezone}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Standard Time (EST)</option>
                  <option value="America/Los_Angeles">Pacific Standard Time (PST)</option>
                  <option value="Europe/London">Greenwich Mean Time (GMT)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="dateFormat">Date Format</label>
                <select
                  id="dateFormat"
                  name="dateFormat"
                  value={settings.dateFormat}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="currency">Currency</label>
                <select
                  id="currency"
                  name="currency"
                  value={settings.currency}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="INR">Indian Rupee (₹)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="sessionTimeout">Session Timeout (minutes)</label>
                <select
                  id="sessionTimeout"
                  name="sessionTimeout"
                  value={settings.sessionTimeout}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="60">60 minutes</option>
                  <option value="120">2 hours</option>
                  <option value="0">Never</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="card-title">Notification Settings</h2>
          <div className="settings-form">
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="notifications"
                  checked={settings.notifications}
                  onChange={handleChange}
                />
                <span>Enable Notifications</span>
              </label>
            </div>

            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="emailNotifications"
                  checked={settings.emailNotifications}
                  onChange={handleChange}
                  disabled={!settings.notifications}
                />
                <span>Email Notifications</span>
              </label>
            </div>

            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="smsNotifications"
                  checked={settings.smsNotifications}
                  onChange={handleChange}
                  disabled={!settings.notifications}
                />
                <span>SMS Notifications</span>
              </label>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="card-title">Appearance</h2>
          <div className="settings-form">
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="darkMode"
                  checked={settings.darkMode}
                  onChange={handleChange}
                />
                <span>Dark Mode</span>
              </label>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="card-title">System Settings</h2>
          <div className="settings-form">
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="autoSave"
                  checked={settings.autoSave}
                  onChange={handleChange}
                />
                <span>Auto-save forms</span>
              </label>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="card-title">Company Information</h2>
          <div className="settings-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="companyName">Company Name</label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={settings.companyName}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter company name"
                />
              </div>
            </div>

            <div className="form-row full-width">
              <div className="form-group">
                <label htmlFor="address">Address</label>
                <textarea
                  id="address"
                  name="address"
                  value={settings.address}
                  onChange={handleChange}
                  className="form-input"
                  rows="3"
                  placeholder="Enter company address"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={settings.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter email address"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={settings.phone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter phone number"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="settings-actions">
          <button type="submit" className="btn btn-primary">
            Save Settings
          </button>
          <button type="button" onClick={handleReset} className="btn btn-secondary" style={{ backgroundColor: '#64748b', color: '#ffffff' }}>
            Reset to Default
          </button>
        </div>
      </form>
    </div>
  );
};

export default Settings;
